import { useEffect, useRef } from "react";

import { useIdleOrHidden } from "../hooks/useIdleOrHidden";
import { useVersionCheck, type UseVersionCheckOptions } from "../hooks/useVersionCheck";

const DEFAULT_MAX_JITTER_MS = 30_000; // LD-4
const DEFAULT_RELOAD_COOLDOWN_MS = 5 * 60 * 1000; // LD-5
const LAST_RELOAD_KEY = "release.lastReloadAt";
const SCROLL_KEY_PREFIX = "release.scroll:";

interface VersionInfo {
  from: string;
  to: string;
}

// Stable module-level defaults so the reload effect's deps don't change every
// render (inline arrow defaults would defeat the dependency array).
const defaultPerformReload = () => window.location.reload();
const defaultRandom = () => Math.random();
const defaultNow = () => Date.now();

function getStorage(provided?: Storage): Storage | null {
  if (provided) return provided;
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    // sessionStorage access can throw in sandboxed iframes.
    return null;
  }
}

function withinCooldown(storage: Storage | null, now: number, cooldownMs: number): boolean {
  if (!storage) return false;
  const raw = storage.getItem(LAST_RELOAD_KEY);
  if (raw === null) return false;
  const last = Number(raw);
  return Number.isFinite(last) && now - last < cooldownMs;
}

function scrollKey(): string {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return `${SCROLL_KEY_PREFIX}${path}`;
}

/** Restore the window scroll position saved just before an auto-reload (same path). */
function restoreScroll(storage: Storage | null): void {
  if (!storage || typeof window === "undefined") return;
  const raw = storage.getItem(scrollKey());
  if (raw === null) return;
  storage.removeItem(scrollKey());
  const y = Number(raw);
  if (Number.isFinite(y)) window.scrollTo(0, y);
}

export interface ReleaseAutoReloadProps extends Pick<
  UseVersionCheckOptions,
  "endpoint" | "pollIntervalMs" | "enabled"
> {
  /** Fired once, the first time a newer build is detected. */
  onNewVersionAvailable?: (info: VersionInfo) => void;
  /** Fired just before the page reloads — the LD-6 breadcrumb point. */
  onBeforeReload?: (info: VersionInfo) => void;
  /**
   * When true, suppress auto-reload even if a new version is available and the
   * tab is idle/hidden (LD-1 dirty-form + LD-2 mutation gate, supplied by the
   * host in Slice 2d). Default false — the portal viewer has no unsaved state.
   */
  reloadBlocked?: boolean;
  /** Idle threshold (LD-3). Default 7 min. */
  idleMs?: number;
  /** Max random jitter before reload (LD-4). Default 30s. */
  maxJitterMs?: number;
  /** Suppress reloads for this long after one fires (LD-5). Default 5 min. */
  reloadCooldownMs?: number;
  // --- test seams (default to real browser primitives) ---
  performReload?: () => void;
  randomFn?: () => number;
  nowFn?: () => number;
  storage?: Storage;
}

/**
 * Watches for a new release and auto-reloads when the user is idle or the tab is hidden (SIM-1670).
 * Gated on `(idle || hidden) && newVersionAvailable && !reloadBlocked` and outside the post-reload
 * cooldown; delayed by random jitter to avoid multi-tab thrash, and cancelled if any gate flips
 * during that window. Scroll is saved and restored; the reload preserves the route.
 */
export function ReleaseAutoReload({
  onNewVersionAvailable,
  onBeforeReload,
  reloadBlocked = false,
  endpoint,
  pollIntervalMs,
  enabled,
  idleMs,
  maxJitterMs = DEFAULT_MAX_JITTER_MS,
  reloadCooldownMs = DEFAULT_RELOAD_COOLDOWN_MS,
  performReload = defaultPerformReload,
  randomFn = defaultRandom,
  nowFn = defaultNow,
  storage,
}: ReleaseAutoReloadProps): null {
  const { newVersionAvailable, loadedBuildId, latestBuildId } = useVersionCheck({
    endpoint,
    pollIntervalMs,
    enabled,
  });
  const idleOrHidden = useIdleOrHidden(idleMs);

  // Restore scroll once on mount — this render may be the fresh load that
  // followed an auto-reload.
  useEffect(() => {
    restoreScroll(getStorage(storage));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; storage ref is stable
  }, []);

  // Detection callback fires once (Slice 2b behavior).
  const firedRef = useRef(false);
  useEffect(() => {
    if (newVersionAvailable && latestBuildId && !firedRef.current) {
      firedRef.current = true;
      onNewVersionAvailable?.({ from: loadedBuildId, to: latestBuildId });
    }
  }, [newVersionAvailable, latestBuildId, loadedBuildId, onNewVersionAvailable]);

  // Reload scheduling.
  useEffect(() => {
    const active = newVersionAvailable && idleOrHidden && !reloadBlocked;
    if (!active || !latestBuildId) return;
    const store = getStorage(storage);
    if (withinCooldown(store, nowFn(), reloadCooldownMs)) return;

    const timer = setTimeout(() => {
      if (store && typeof window !== "undefined") {
        store.setItem(LAST_RELOAD_KEY, String(nowFn()));
        store.setItem(scrollKey(), String(window.scrollY));
      }
      onBeforeReload?.({ from: loadedBuildId, to: latestBuildId });
      performReload();
    }, randomFn() * maxJitterMs);

    return () => clearTimeout(timer);
  }, [
    newVersionAvailable,
    idleOrHidden,
    reloadBlocked,
    latestBuildId,
    loadedBuildId,
    maxJitterMs,
    reloadCooldownMs,
    storage,
    nowFn,
    randomFn,
    onBeforeReload,
    performReload,
  ]);

  return null;
}
